import {spacing} from '@nfq/react-grid';

import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

interface ButtonWidthProps {
    /** Indicates if the button is an icon button. */
    $isIconButton?: boolean;
    /** Size of the button. */
    $size: 'large' | 'small';
    /** Width behavior of the button. */
    $width: 'auto' | 'full';
}

interface ButtonPaddingProps {
    /** Indicates if the button is an icon button. */
    $isIconButton?: boolean;
    /** Indicates if the button is an link button. */
    $isLink?: boolean;
    /** Size of the button. */
    $size: 'large' | 'small';
}

/**
 * Generates CSS variables for button base states (default appearance).
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color values.
 * @returns CSS string with variable definitions for different theme/variant combinations.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    &[data-theme=light][data-variant=primary] {
        --bg-color: ${theme.colors.primary};
        --border-color: ${theme.colors.primary};
        --font-color: ${theme.colors.neutral0};
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: transparent;
        --border-color: transparent;
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=primary] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: transparent;
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.neutral0};
    }
`;

/**
 * Generates CSS variables for button hover states.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color values.
 * @returns CSS string with variable definitions for hover state theme/variant combinations.
 */
export const getHoverVariables = ({theme}: VariablesProps) => `
    &[data-theme=light][data-variant=primary] {
        --bg-color: ${theme.colors.buttonPrimaryLightHover};
        --border-color: ${theme.colors.buttonPrimaryLightHover};
        --font-color: ${theme.colors.neutral0};
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral20};
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=primary] {
        --bg-color: ${theme.colors.neutral20};
        --border-color: ${theme.colors.neutral20};
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=secondary]{
        --bg-color: ${theme.colors.buttonSecondaryDarkHover};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.neutral0};
    }
`;

/**
 * Generates CSS variables for button focus states.
 *
 * @param props       The properties object.
 * @param props.theme The theme object containing color values.
 * @returns CSS string with variable definitions for focus state theme/variant combinations.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    &[data-theme=light][data-variant=primary] {
        --bg-color: ${theme.colors.primary};
        --border-color: ${theme.colors.primary};
        --font-color: ${theme.colors.neutral0};
    }
    &[data-theme=light][data-variant=secondary] {
        --bg-color: transparent;
        --border-color: transparent;
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=primary] {
        --bg-color: ${theme.colors.neutral0};
        --border-color: ${theme.colors.neutral0};
        --font-color: ${theme.colors.primary};
    }
    &[data-theme=dark][data-variant=secondary] {
        --bg-color: transparent;
        --border-color: transparent;
        --font-color: ${theme.colors.neutral0};
    }
`;

/**
 * Calculates the appropriate width for a button based on its type and size.
 *
 * @param props               The button width properties.
 * @param props.$isIconButton Whether this is an icon-only button.
 * @param props.$size         The size of the button ('large' or 'small').
 * @param props.$width        The width behavior ('auto' or 'full').
 * @returns The CSS width value as a string.
 */
export const getButtonWidth = ({$isIconButton, $size, $width}: ButtonWidthProps) => {
    if ($isIconButton) {
        return $size === 'small' ? '3.6rem' : '4.8rem';
    } else if ($width === 'full') {
        return '100%';
    }

    return 'fit-content';
};

/**
 * Calculates the appropriate padding for a button based on its type and size.
 *
 * @param props               The button padding properties.
 * @param props.$isIconButton Whether this is an icon-only button.
 * @param props.$size         The size of the button ('large' or 'small').
 * @returns The CSS padding value as a string in the format "vertical horizontal".
 */
export const getButtonPadding = ({$isIconButton, $size}: ButtonPaddingProps) => {
    let verticalPadding: string;
    let horizontalPadding: string;

    if ($isIconButton) {
        verticalPadding = $size === 'small' ? `${spacing(1)}` : `${spacing(3)}`;
        horizontalPadding = verticalPadding;
    } else {
        verticalPadding = $size === 'small' ? `${spacing(2)}` : `${spacing(3)}`;
        horizontalPadding = $size === 'small' ? `${spacing(3)}` : `${spacing(6)}`;
    }

    return `${verticalPadding} ${horizontalPadding}`;
};